import React, { useRef, useState } from 'react';
import SkillEditorBody from './SkillEditorBody';
import AgentBundleItemModal, { type BundleItem } from './AgentBundleItemModal';
import BundledIntegrationChips from './BundledIntegrationChips';
import type { BundledIntegration } from '../features/claudeagents/claudeAgentApi';

export interface AgentDraftPreviewProps {
  system: string;
  skills: { name: string; content: string }[];
  mcpServers: { name: string; url: string; needs_auth: boolean; platform_provided?: boolean }[];
  integrations?: BundledIntegration[];
  userRules?: string | null;
  /** Drop the skill/MCP/integration chips - for a store page that already lists them. */
  instructionsOnly?: boolean;
}

const chipStyle: React.CSSProperties = { cursor: 'pointer', border: 'none', font: 'inherit' };

// Local-state counterpart to AgentBundlePreview — same visual shape (read-only
// system prompt, skill chips, MCP chips) but sourced directly from in-progress
// wizard state instead of fetching an already-minted share token. Used while
// authoring a brand-new agent from scratch, before anything has been saved —
// there's no token to resolve yet.
const AgentDraftPreview: React.FC<AgentDraftPreviewProps> = ({ system, skills, mcpServers, integrations, userRules, instructionsOnly = false }) => {
  const previewEditorRef = useRef<HTMLTextAreaElement>(null);
  const rulesEditorRef = useRef<HTMLTextAreaElement>(null);
  const [selectedItem, setSelectedItem] = useState<BundleItem | null>(null);

  if (instructionsOnly && !system && !userRules)
    return <p style={{ color: 'var(--text-tertiary)', fontSize: 12.5, margin: 0 }}>This agent has no instructions of its own.</p>;

  return (
    <>
      {system && (
        <div>
          <div className="mt-detail-field-label">agents.md</div>
          <SkillEditorBody
            mode="preview"
            onModeChange={() => {}}
            body={system}
            onBodyChange={() => {}}
            textEditorRef={previewEditorRef}
            insertMd={() => {}}
            readOnly
            hideModeTabs
          />
        </div>
      )}
      {userRules && (
        <div>
          <div className="mt-detail-field-label">Your Rules</div>
          <SkillEditorBody
            mode="preview"
            onModeChange={() => {}}
            body={userRules}
            onBodyChange={() => {}}
            textEditorRef={rulesEditorRef}
            insertMd={() => {}}
            readOnly
            hideModeTabs
          />
        </div>
      )}
      {!instructionsOnly && skills.length > 0 && (
        <div>
          <div className="mt-detail-field-label">Bundled skills</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {skills.map(s => (
              <button key={s.name} type="button" onClick={() => setSelectedItem({ kind: 'skill', name: s.name, content: s.content })}
                style={{ ...chipStyle, background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.22)', color: '#fb923c', fontSize: 11, padding: '2px 8px', borderRadius: 5 }}>
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {!instructionsOnly && mcpServers.length > 0 && (
        <div>
          <div className="mt-detail-field-label">Bundled MCPs</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {mcpServers.map(m => (
              <button key={m.url} type="button" title={m.needs_auth && !m.platform_provided ? 'Requires credentials' : undefined}
                onClick={() => setSelectedItem({ kind: 'mcp', name: m.name, url: m.url, needs_auth: m.needs_auth, platform_provided: m.platform_provided })}
                style={{ ...chipStyle, background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.22)', color: '#60a5fa', fontSize: 11, padding: '2px 8px', borderRadius: 5 }}>
                {m.name}{m.needs_auth && !m.platform_provided ? ' 🔑' : ''}
              </button>
            ))}
          </div>
        </div>
      )}
      {!instructionsOnly && <BundledIntegrationChips integrations={integrations} onSelect={integration => setSelectedItem({ kind: 'integration', integration })} />}
      {selectedItem && <AgentBundleItemModal item={selectedItem} onClose={() => setSelectedItem(null)} />}
    </>
  );
};

export default AgentDraftPreview;
